// Slice 10 — AI Suite tenant config + live tester.
// Switchable STT engine (Simulator / OpenAI Whisper via Emergent / Deepgram via tenant key)
// and switchable LLM provider/model (Claude / GPT / Gemini via Emergent).
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, Save, Brain, Languages, Sparkles, KeyRound, FlaskConical,
  ShieldAlert, Mic, Send, Wand2, Eye, EyeOff, BookOpen, RefreshCw, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

function pairKey(p) { return `${p.provider}/${p.model}`; }

export default function AiSuitePage() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { canWrite } = useCurrentModulePerms();
  const [revealKey, setRevealKey] = useState(false);
  const [dgInput, setDgInput] = useState("");        // staged plaintext for Deepgram
  const [sxInput, setSxInput] = useState("");        // staged plaintext for Soniox
  const [llmKeyInput, setLlmKeyInput] = useState("");// staged plaintext for own LLM key
  const [llmPair, setLlmPair] = useState("");        // "provider/model"

  // Tester state
  const [sttFile, setSttFile] = useState(null);
  const [sttResult, setSttResult] = useState(null);
  const [sttBusy, setSttBusy] = useState(false);
  const [sentText, setSentText] = useState("This is the worst service I have ever used.");
  const [sentResult, setSentResult] = useState(null);
  const [sentBusy, setSentBusy] = useState(false);
  const [copilotMsg, setCopilotMsg] = useState("My account is locked, can you help?");
  const [copilotTone, setCopilotTone] = useState("warm-professional");
  const [copilotResult, setCopilotResult] = useState(null);
  const [copilotBusy, setCopilotBusy] = useState(false);
  const [redactText, setRedactText] = useState("Reach me at jose@haloocom.com or +91 98765 43210, card 4111 1111 1111 1111.");
  const [redactResult, setRedactResult] = useState(null);
  const [redactBusy, setRedactBusy] = useState(false);
  // KB
  const [kbStatus, setKbStatus] = useState(null);
  const [kbBusy, setKbBusy] = useState(false);
  const [kbQuery, setKbQuery] = useState("how much does the pro plan cost?");
  const [kbHits, setKbHits] = useState(null);
  const [kbSearchBusy, setKbSearchBusy] = useState(false);
  // Copilot KB toggle
  const [copilotUseKB, setCopilotUseKB] = useState(true);

  const fileInputRef = useRef(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/ai/config");
      setCfg(r.data);
      setLlmPair(`${r.data.llm_provider}/${r.data.llm_model}`);
      setDgInput(""); // never preload secret
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
    loadKbStatus();
  }
  async function loadKbStatus() {
    try { const r = await api.get("/ai/kb/status"); setKbStatus(r.data); }
    catch (e) { /* silent */ }
  }
  async function reindexKb() {
    setKbBusy(true);
    try {
      const r = await api.post("/ai/kb/reindex");
      setKbStatus(r.data);
      toast.success(`Re-indexed: ${r.data.docs_count} docs · ${r.data.chunks_count} chunks`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setKbBusy(false); }
  }
  async function runKbSearch() {
    if (!kbQuery.trim()) return;
    setKbSearchBusy(true); setKbHits(null);
    try {
      const r = await api.post("/ai/kb/search", { query: kbQuery, top_k: 5 });
      setKbHits(r.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setKbSearchBusy(false); }
  }
  useEffect(() => { load(); }, []);

  const sttEngines = cfg?.supported_stt_engines || [];
  const llmList   = cfg?.supported_llms || [];
  const activeEngine = sttEngines.find((e) => e.engine === cfg?.stt_engine);
  const deepgramSelected = cfg?.stt_engine === "deepgram";
  const dgWarn = deepgramSelected && !cfg?.deepgram_key_present && !dgInput.trim();
  const sonioxSelected = cfg?.stt_engine === "soniox";
  const sxWarn = sonioxSelected && !cfg?.soniox_key_present && !sxInput.trim();

  async function save() {
    if (!cfg) return;
    setSaving(true);
    try {
      const [prov, mdl] = llmPair.split("/");
      const body = {
        stt_engine: cfg.stt_engine,
        stt_language: cfg.stt_language,
        llm_provider: prov,
        llm_model: mdl,
        deepgram_model: cfg.deepgram_model,
        soniox_model: cfg.soniox_model,
        pii_redaction_enabled: cfg.pii_redaction_enabled,
        copilot_auto_regenerate: cfg.copilot_auto_regenerate,
      };
      if (dgInput.trim()) body.deepgram_key = dgInput.trim();
      if (sxInput.trim()) body.soniox_key = sxInput.trim();
      if (llmKeyInput.trim()) body.llm_api_key = llmKeyInput.trim();
      const r = await api.put("/ai/config", body);
      setCfg(r.data);
      setLlmPair(`${r.data.llm_provider}/${r.data.llm_model}`);
      setDgInput("");
      setSxInput("");
      setLlmKeyInput("");
      toast.success("AI Suite configuration saved");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  }

  async function runSttUpload() {
    if (!sttFile) { toast.error("Pick an audio file first"); return; }
    setSttBusy(true); setSttResult(null);
    try {
      const fd = new FormData();
      fd.append("file", sttFile);
      const r = await api.post("/ai/stt/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setSttResult(r.data);
      toast.success(`Transcribed via ${r.data.engine}`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSttBusy(false); }
  }

  async function runSttSim() {
    setSttBusy(true); setSttResult(null);
    try {
      const r = await api.post("/ai/stt/transcribe",
        { simulator_hint: "Test caller", engine_override: "simulator" });
      setSttResult(r.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSttBusy(false); }
  }

  async function runSentiment() {
    setSentBusy(true); setSentResult(null);
    try {
      const r = await api.post("/ai/sentiment", { text: sentText });
      setSentResult(r.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSentBusy(false); }
  }

  async function runCopilot() {
    setCopilotBusy(true); setCopilotResult(null);
    try {
      const r = await api.post("/ai/copilot/compose", {
        history: [{ from: "customer", body: copilotMsg }],
        tone: copilotTone,
        use_kb: copilotUseKB,
      });
      setCopilotResult(r.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setCopilotBusy(false); }
  }

  async function runRedact() {
    setRedactBusy(true); setRedactResult(null);
    try {
      const r = await api.post("/ai/redact", { text: redactText });
      setRedactResult(r.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setRedactBusy(false); }
  }

  if (loading || !cfg) {
    return (
      <div className="py-12 text-center">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      </div>
    );
  }

  const sentColor = sentResult?.label === "negative" || sentResult?.label === "frustrated"
    ? "bg-red-100 text-red-700 border-red-200"
    : sentResult?.label === "positive" || sentResult?.label === "happy"
    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : "bg-slate-100 text-slate-700 border-slate-200";

  return (
    <div className="px-4 sm:px-6 py-6 max-w-6xl" data-testid="ai-suite-page">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="ai-suite-title">
            AI Suite
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Switchable Speech-to-Text and Large-Language-Model providers — used by
            Voicemail Transcription, Copilot Compose, Sentiment, and Summarisation.
          </p>
        </div>
        {canWrite && <Button
          data-testid="ai-suite-save"
          onClick={save}
          disabled={saving}
          className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
          Save
        </Button>}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* STT card */}
        <Card className="p-5 space-y-4" data-testid="ai-suite-stt-card">
          <div className="flex items-center gap-2">
            <Languages className="w-4 h-4 text-primary" />
            <h2 className="font-display font-bold">Speech-to-Text Engine</h2>
            <Badge variant="outline" className="ml-auto text-[10px] uppercase tracking-wider">
              {cfg.stt_engine}
            </Badge>
          </div>

          <div>
            <Label className="mb-2 block">Active STT engine</Label>
            <Select value={cfg.stt_engine} onValueChange={(v) => setCfg({ ...cfg, stt_engine: v })}>
              <SelectTrigger data-testid="ai-stt-engine"><SelectValue /></SelectTrigger>
              <SelectContent>
                {sttEngines.map((e) => (
                  <SelectItem key={e.engine} value={e.engine}>
                    {e.label}{" "}
                    <span className="text-xs text-muted-foreground">
                      ({e.via.replace("_", " ")}{e.key_required ? " · key required" : ""})
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-2">
              {activeEngine?.via === "emergent_key" &&
                "Uses your Emergent universal key. Credits deducted on each call."}
              {activeEngine?.via === "user_key" &&
                (sonioxSelected
                  ? "Uses your own Soniox API key (paste below) for real-time live call transcription. Never leaves the server unencrypted."
                  : "Uses your own Deepgram API key (paste below). Never leaves the server unencrypted.")}
              {activeEngine?.via === "internal" &&
                "Free internal simulator — emits a canned transcript. Good for demos / load-tests."}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Language (ISO 639-1)</Label>
              <Input
                data-testid="ai-stt-language"
                value={cfg.stt_language}
                onChange={(e) => setCfg({ ...cfg, stt_language: e.target.value })}
                placeholder="en"
              />
            </div>
            <div>
              <Label>{sonioxSelected ? "Soniox model (real-time)" : "Deepgram model"}</Label>
              {sonioxSelected ? (
                <Input
                  data-testid="ai-stt-sx-model"
                  value={cfg.soniox_model || ""}
                  onChange={(e) => setCfg({ ...cfg, soniox_model: e.target.value })}
                  placeholder="stt-rt-v5"
                />
              ) : (
                <Input
                  data-testid="ai-stt-dg-model"
                  value={cfg.deepgram_model}
                  onChange={(e) => setCfg({ ...cfg, deepgram_model: e.target.value })}
                  placeholder="nova-2"
                />
              )}
            </div>
          </div>

          {sonioxSelected && (
            <div>
              <Label className="flex items-center gap-2">
                <KeyRound className="w-3.5 h-3.5" /> Soniox API key
                {cfg.soniox_key_present && (
                  <Badge variant="outline" className="ml-2 text-[10px]">
                    {cfg.soniox_key_masked ? `Stored: ${cfg.soniox_key_masked}` : "Using server key"}
                  </Badge>
                )}
              </Label>
              <div className="flex gap-2 mt-1">
                <Input
                  data-testid="ai-stt-sx-key"
                  type={revealKey ? "text" : "password"}
                  placeholder={cfg.soniox_key_present ? "(leave blank to keep stored key)" : "Paste your Soniox key"}
                  value={sxInput}
                  onChange={(e) => setSxInput(e.target.value)}
                  className="font-mono"
                />
                <Button
                  variant="outline"
                  onClick={() => setRevealKey((v) => !v)}
                  type="button"
                  data-testid="ai-stt-sx-reveal"
                >
                  {revealKey ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                </Button>
              </div>
              {sxWarn && (
                <div className="mt-2 flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
                  <ShieldAlert className="w-3.5 h-3.5 mt-0.5"/>
                  Soniox is selected but no key is stored — live transcription stays off
                  until you paste a key here.
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Get a key at <span className="font-mono">console.soniox.com</span>. Use a
                <span className="font-mono"> stt-rt-*</span> real-time model (async models like
                <span className="font-mono"> stt-async-v5</span> won't work for live calls).
                The key is encrypted at rest (Fernet) — only the last 4 chars are shown.
              </p>
            </div>
          )}

          {deepgramSelected && (
          <div>
            <Label className="flex items-center gap-2">
              <KeyRound className="w-3.5 h-3.5" /> Deepgram API key
              {cfg.deepgram_key_present && (
                <Badge variant="outline" className="ml-2 text-[10px]">
                  Stored: {cfg.deepgram_key_masked}
                </Badge>
              )}
            </Label>
            <div className="flex gap-2 mt-1">
              <Input
                data-testid="ai-stt-dg-key"
                type={revealKey ? "text" : "password"}
                placeholder={cfg.deepgram_key_present ? "(leave blank to keep stored key)" : "Paste your Deepgram key"}
                value={dgInput}
                onChange={(e) => setDgInput(e.target.value)}
                className="font-mono"
              />
              <Button
                variant="outline"
                onClick={() => setRevealKey((v) => !v)}
                type="button"
                data-testid="ai-stt-dg-reveal"
              >
                {revealKey ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
              </Button>
            </div>
            {dgWarn && (
              <div className="mt-2 flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
                <ShieldAlert className="w-3.5 h-3.5 mt-0.5"/>
                Deepgram is selected but no key is stored — calls will fall back to the
                simulator until you paste a key here.
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Get a key at <span className="font-mono">console.deepgram.com</span>.
              The key is encrypted at rest (Fernet) — only the last 4 chars are shown.
            </p>
          </div>
          )}
        </Card>

        {/* LLM card */}
        <Card className="p-5 space-y-4" data-testid="ai-suite-llm-card">
          <div className="flex items-center gap-2">
            <Brain className="w-4 h-4 text-primary" />
            <h2 className="font-display font-bold">Large-Language-Model Provider</h2>
            <Badge variant="outline" className="ml-auto text-[10px] uppercase tracking-wider">
              {cfg.llm_provider}
            </Badge>
          </div>

          <div>
            <Label className="mb-2 block">Active model (used by Sentiment / Copilot / Summarise)</Label>
            <Select value={llmPair} onValueChange={setLlmPair}>
              <SelectTrigger data-testid="ai-llm-pair"><SelectValue /></SelectTrigger>
              <SelectContent>
                {llmList.map((p) => (
                  <SelectItem key={pairKey(p)} value={pairKey(p)}>
                    {p.label}{" "}
                    <span className="text-xs text-muted-foreground">({p.provider})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-2">
              Providers route through your <span className="font-mono">EMERGENT_LLM_KEY</span> by
              default. For self-hosted deployments, paste your own provider key below to call the
              provider directly. Switch any time — Copilot picks up the change instantly.
            </p>
          </div>

          {/* Own LLM provider key (BYO — required for self-hosted) */}
          <div>
            <Label className="flex items-center gap-2">
              <KeyRound className="w-3.5 h-3.5" /> Own {cfg.llm_provider} API key
              <span className="text-[10px] text-muted-foreground">(optional)</span>
              {cfg.llm_key_present && (
                <Badge variant="outline" className="ml-2 text-[10px]">
                  Stored: {cfg.llm_key_masked}
                </Badge>
              )}
            </Label>
            <div className="flex gap-2 mt-1">
              <Input
                data-testid="ai-llm-key"
                type={revealKey ? "text" : "password"}
                placeholder={cfg.llm_key_present ? "(leave blank to keep stored key)" : `Paste your ${cfg.llm_provider} API key (or leave blank to use EMERGENT_LLM_KEY)`}
                value={llmKeyInput}
                onChange={(e) => setLlmKeyInput(e.target.value)}
                className="font-mono"
              />
              <Button
                variant="outline"
                onClick={() => setRevealKey((v) => !v)}
                type="button"
                data-testid="ai-llm-key-reveal"
              >
                {revealKey ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Anthropic: <span className="font-mono">console.anthropic.com</span> ·
              OpenAI: <span className="font-mono">platform.openai.com</span> ·
              Gemini: <span className="font-mono">aistudio.google.com</span>.
              Encrypted at rest (Fernet) — only the last 4 chars are shown. Ensure the selected
              model is valid for your account.
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Switch
              data-testid="ai-pii-toggle"
              checked={!!cfg.pii_redaction_enabled}
              onCheckedChange={(v) => setCfg({ ...cfg, pii_redaction_enabled: !!v })}
            />
            <div>
              <Label>PII redaction enabled</Label>
              <p className="text-xs text-muted-foreground">
                Default rules: email · phone · credit-card · SSN · Aadhaar. Add custom regex
                in <span className="font-mono">PII Redaction</span>.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Switch
              data-testid="ai-copilot-autoregen-toggle"
              checked={!!cfg.copilot_auto_regenerate}
              onCheckedChange={(v) => setCfg({ ...cfg, copilot_auto_regenerate: !!v })}
            />
            <div>
              <Label>Auto-regenerate Copilot on channel switch</Label>
              <p className="text-xs text-muted-foreground">
                When ON, the AI Copilot automatically drafts a new suggested reply each time an
                agent switches channel (e.g. WhatsApp → Instagram). When OFF, agents click
                <span className="font-mono"> Regenerate</span> to refresh it (fewer LLM calls).
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* KB status strip */}
      <Card className="p-4 mt-5 flex flex-wrap items-center gap-4" data-testid="ai-suite-kb-status">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-primary"/>
          <h3 className="font-display font-bold">Knowledge Base RAG</h3>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Badge variant="outline" className="text-[10px]">
            <span className="font-mono mr-1">{kbStatus?.docs_count ?? "—"}</span> docs
          </Badge>
          <Badge variant="outline" className="text-[10px]">
            <span className="font-mono mr-1">{kbStatus?.chunks_count ?? "—"}</span> chunks
          </Badge>
          <Badge variant="outline" className="text-[10px]">
            <span className="font-mono mr-1">{kbStatus?.vocab_size ?? "—"}</span> vocab
          </Badge>
          {kbStatus?.indexed_at && (
            <span className="text-muted-foreground">
              indexed: {new Date(kbStatus.indexed_at).toLocaleString()}
            </span>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            data-testid="ai-kb-reindex"
            onClick={reindexKb}
            disabled={kbBusy}
            variant="outline"
            className="gap-1.5"
          >
            {kbBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
            Reindex
          </Button>
          <Button
            data-testid="ai-kb-open"
            onClick={() => window.location.assign("/admin/kb")}
            variant="outline"
            className="gap-1.5"
          >
            Manage docs
          </Button>
        </div>
      </Card>

      {/* Live testers */}
      <h2 className="font-display text-xl font-bold tracking-tight mt-8 mb-3 flex items-center gap-2">
        <FlaskConical className="w-5 h-5 text-primary"/> Live testers
      </h2>
      <div className="grid lg:grid-cols-2 gap-5">

        {/* STT tester */}
        <Card className="p-5 space-y-3" data-testid="ai-test-stt">
          <div className="flex items-center gap-2">
            <Mic className="w-4 h-4 text-primary"/>
            <h3 className="font-display font-bold">Transcribe audio (active engine)</h3>
          </div>
          <input
            ref={fileInputRef}
            data-testid="ai-test-stt-file"
            type="file"
            accept="audio/*"
            onChange={(e) => setSttFile(e.target.files?.[0] || null)}
            className="block text-sm text-muted-foreground file:mr-3 file:px-3 file:py-1.5
                       file:rounded-md file:border-0 file:bg-secondary file:text-secondary-foreground"
          />
          <div className="flex gap-2">
            <Button
              data-testid="ai-test-stt-run"
              onClick={runSttUpload}
              disabled={!sttFile || sttBusy}
              className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"
            >
              {sttBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
              Run
            </Button>
            <Button variant="outline" onClick={runSttSim} disabled={sttBusy} data-testid="ai-test-stt-sim">
              Simulator only
            </Button>
          </div>
          {sttResult && (
            <div className="rounded-md border border-border bg-card/50 p-3 text-sm space-y-1" data-testid="ai-test-stt-result">
              <div className="flex flex-wrap gap-2 items-center">
                <Badge variant="outline" className="text-[10px] uppercase">engine: {sttResult.engine}</Badge>
                {sttResult.fallback_reason && (
                  <Badge variant="outline" className="text-[10px] bg-amber-50">fallback: {sttResult.fallback_reason}</Badge>
                )}
                {typeof sttResult.confidence === "number" && (
                  <Badge variant="outline" className="text-[10px]">conf: {(sttResult.confidence * 100).toFixed(0)}%</Badge>
                )}
              </div>
              <p className="leading-relaxed">{sttResult.text || "(empty)"}</p>
            </div>
          )}
        </Card>

        {/* Sentiment tester */}
        <Card className="p-5 space-y-3" data-testid="ai-test-sentiment">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary"/>
            <h3 className="font-display font-bold">Sentiment (active LLM)</h3>
          </div>
          <Textarea
            data-testid="ai-test-sentiment-input"
            rows={3}
            value={sentText}
            onChange={(e) => setSentText(e.target.value)}
          />
          <Button
            data-testid="ai-test-sentiment-run"
            onClick={runSentiment}
            disabled={sentBusy || !sentText.trim()}
            className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"
          >
            {sentBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>} Analyse
          </Button>
          {sentResult && (
            <div className="rounded-md border border-border bg-card/50 p-3 text-sm" data-testid="ai-test-sentiment-result">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`px-2 py-0.5 rounded-md text-xs font-semibold border ${sentColor}`}>
                  {sentResult.label}
                </span>
                <Badge variant="outline" className="text-[10px]">
                  score: {(sentResult.score * 100).toFixed(0)}%
                </Badge>
                {sentResult.is_mocked && (
                  <Badge variant="outline" className="text-[10px] bg-amber-50">mock fallback</Badge>
                )}
                {sentResult.model && (
                  <Badge variant="outline" className="text-[10px]">{sentResult.model}</Badge>
                )}
              </div>
              <p className="mt-2">{sentResult.summary}</p>
            </div>
          )}
        </Card>

        {/* Copilot tester */}
        <Card className="p-5 space-y-3" data-testid="ai-test-copilot">
          <div className="flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-primary"/>
            <h3 className="font-display font-bold">Copilot — Compose reply</h3>
          </div>
          <div>
            <Label className="mb-1.5 block text-xs uppercase tracking-wider text-muted-foreground">Voice style</Label>
            <Select value={copilotTone} onValueChange={setCopilotTone}>
              <SelectTrigger data-testid="ai-test-copilot-tone"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(cfg.tones || []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.label}
                    <span className="text-xs text-muted-foreground ml-2">— {t.description}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Textarea
            data-testid="ai-test-copilot-input"
            rows={3}
            value={copilotMsg}
            onChange={(e) => setCopilotMsg(e.target.value)}
            placeholder="Customer message…"
          />
          <div className="flex items-center gap-2">
            <Switch
              data-testid="ai-test-copilot-use-kb"
              checked={copilotUseKB}
              onCheckedChange={setCopilotUseKB}
            />
            <Label className="text-xs">Use Knowledge Base (RAG)</Label>
          </div>
          <Button
            data-testid="ai-test-copilot-run"
            onClick={runCopilot}
            disabled={copilotBusy || !copilotMsg.trim()}
            className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"
          >
            {copilotBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>} Compose
          </Button>
          {copilotResult && (
            <div className="rounded-md border border-border bg-card/50 p-3 text-sm space-y-2" data-testid="ai-test-copilot-result">
              <div className="flex flex-wrap items-center gap-2">
                {copilotResult.tone_label && (
                  <Badge variant="outline" className="text-[10px] bg-secondary/40">{copilotResult.tone_label}</Badge>
                )}
                {copilotResult.model && <Badge variant="outline" className="text-[10px]">{copilotResult.model}</Badge>}
                {copilotResult.kb_used && (
                  <Badge variant="outline" className="text-[10px] bg-emerald-50 dark:bg-emerald-500/20 border-emerald-200 text-emerald-700 dark:text-emerald-300">
                    {(copilotResult.citations || []).length} KB cite{(copilotResult.citations || []).length === 1 ? "" : "s"}
                  </Badge>
                )}
                {copilotResult.is_mocked && <Badge variant="outline" className="text-[10px] bg-amber-50">mock fallback</Badge>}
              </div>
              <p className="italic leading-relaxed">"{copilotResult.suggestion}"</p>
              {(copilotResult.citations || []).length > 0 && (
                <div className="space-y-1 pt-1 border-t border-border/60">
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">Sources</div>
                  {copilotResult.citations.map((c, i) => (
                    <a
                      key={c.doc_id + i}
                      href={c.source_url || "#"}
                      target="_blank"
                      rel="noreferrer noopener"
                      onClick={(e) => { if (!c.source_url) e.preventDefault(); }}
                      className="block rounded-md px-2 py-1.5 hover:bg-card transition-colors"
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-bold text-accent shrink-0">[doc{i + 1}]</span>
                        <span className="text-xs font-medium truncate flex-1">{c.title}</span>
                        {c.source_url && <ExternalLink className="w-3 h-3 text-muted-foreground"/>}
                        <span className="text-[10px] text-muted-foreground">{(c.score * 100).toFixed(0)}%</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2 leading-snug">{c.snippet}</p>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* KB Search tester */}
        <Card className="p-5 space-y-3" data-testid="ai-test-kb">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-primary"/>
            <h3 className="font-display font-bold">Knowledge Base — Search</h3>
            <Badge variant="outline" className="ml-auto text-[10px]">TF-IDF</Badge>
          </div>
          <Textarea
            data-testid="ai-test-kb-query"
            rows={2}
            value={kbQuery}
            onChange={(e) => setKbQuery(e.target.value)}
            placeholder="Ask a question…"
          />
          <Button
            data-testid="ai-test-kb-run"
            onClick={runKbSearch}
            disabled={kbSearchBusy || !kbQuery.trim()}
            className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"
          >
            {kbSearchBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>} Search
          </Button>
          {kbHits && (
            <div className="rounded-md border border-border bg-card/50 p-3 text-sm space-y-2" data-testid="ai-test-kb-result">
              <div className="text-xs text-muted-foreground">
                {kbHits.count} hit{kbHits.count === 1 ? "" : "s"} for <span className="font-mono">"{kbHits.query}"</span>
              </div>
              {kbHits.hits.map((c, i) => (
                <a
                  key={c.doc_id + i}
                  href={c.source_url || "#"}
                  target="_blank"
                  rel="noreferrer noopener"
                  onClick={(e) => { if (!c.source_url) e.preventDefault(); }}
                  className="block rounded-md px-2 py-1.5 hover:bg-card transition-colors"
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold text-accent shrink-0">[doc{i + 1}]</span>
                    <span className="text-xs font-medium truncate flex-1">{c.title}</span>
                    {c.source_url && <ExternalLink className="w-3 h-3 text-muted-foreground"/>}
                    <span className="text-[10px] text-muted-foreground">{(c.score * 100).toFixed(0)}%</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2 leading-snug">{c.snippet}</p>
                  {(c.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {c.tags.map((t) => (
                        <Badge key={t} variant="outline" className="text-[9px] py-0">{t}</Badge>
                      ))}
                    </div>
                  )}
                </a>
              ))}
              {kbHits.count === 0 && (
                <p className="text-xs text-muted-foreground italic">No matches — try different keywords or add KB docs in Admin → Knowledge Base.</p>
              )}
            </div>
          )}
        </Card>

        {/* PII Redaction tester */}
        <Card className="p-5 space-y-3" data-testid="ai-test-redact">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-primary"/>
            <h3 className="font-display font-bold">PII Redaction</h3>
          </div>
          <Textarea
            data-testid="ai-test-redact-input"
            rows={3}
            value={redactText}
            onChange={(e) => setRedactText(e.target.value)}
          />
          <Button
            data-testid="ai-test-redact-run"
            onClick={runRedact}
            disabled={redactBusy || !redactText.trim()}
            className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"
          >
            {redactBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>} Redact
          </Button>
          {redactResult && (
            <div className="rounded-md border border-border bg-card/50 p-3 text-sm space-y-2" data-testid="ai-test-redact-result">
              <p className="font-mono text-xs whitespace-pre-wrap break-words">{redactResult.redacted}</p>
              <div className="flex flex-wrap items-center gap-2">
                {(redactResult.hits || []).map((h, i) => (
                  <Badge key={i} variant="outline" className="text-[10px]">
                    {h.label} × {h.count}
                  </Badge>
                ))}
                {(redactResult.hits || []).length === 0 && (
                  <span className="text-xs text-muted-foreground">No PII detected.</span>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
